import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

interface UserProfile {
  id: string;
  role: string;
  email?: string;
  full_name?: string;
  avatar_url?: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
}

export type ProfileUpdates = Partial<
  Pick<
    UserProfile,
    | 'full_name'
    | 'first_name'
    | 'last_name'
    | 'company_name'
    | 'phone'
    | 'address'
    | 'city'
    | 'state'
    | 'zip_code'
    | 'country'
    | 'avatar_url'
  >
>;

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  /** True once the profile fetch for the current user has settled. */
  profileReady: boolean;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<string | null>;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<boolean>;
  updateProfile: (updates: ProfileUpdates) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  hasRole: (roles: string[]) => boolean;
  logAudit: (action: string, resource: string, resourceId?: string, details?: any) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileReady, setProfileReady] = useState(true);

  // For demo purposes only — the real role always comes from the user_profiles table
  const getDemoRole = () => {
    return localStorage.getItem('userRole') || 'admin';
  };

  // Track the current user id in a ref so the auth listener (which cannot
  // use stale closures) can tell identity changes apart from token refreshes.
  const userIdRef = useRef<string | null>(null);

  // Apply a user change atomically:
  // - Same identity (e.g. TOKEN_REFRESHED, SIGNED_IN again): keep the existing
  //   user object and profile so the app never unmounts the current screen.
  // - Identity change: reset the profile and mark it not-ready in the same
  //   batched update, so route guards wait instead of redirecting mid-restore.
  const applyUser = useCallback((nextUser: User | null) => {
    const prevId = userIdRef.current;
    const nextId = nextUser?.id ?? null;
    if (prevId === nextId) {
      // Same identity — keep the existing user object (React bails out on
      // the identical reference), so no spinner flash or lost screen state.
      setUser((prev) => (prev && nextUser && prev.id === nextUser.id ? prev : nextUser));
      return;
    }
    userIdRef.current = nextId;
    if (nextId) {
      setProfile(null);
      setProfileReady(false);
    } else {
      setProfile(null);
      setProfileReady(true);
    }
    setUser(nextUser);
  }, []);

  // Fetch the user's role from the user_profiles table (real mode only).
  // Returns null when the profile cannot be loaded or does not exist —
  // callers must treat a missing profile as "not fully set up", never as a role.
  const fetchProfile = async (authUser: User): Promise<UserProfile | null> => {
    try {
      // Select * so the app keeps working even before the extended-profile
      // columns are added to the database (migration-extended-profiles.sql).
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle();
      if (error) throw error;
      if (data) {
        return {
          id: data.id,
          role: data.role,
          full_name: data.full_name ?? undefined,
          email: data.email,
          avatar_url: data.avatar_url ?? undefined,
          first_name: data.first_name ?? undefined,
          last_name: data.last_name ?? undefined,
          company_name: data.company_name ?? undefined,
          phone: data.phone ?? undefined,
          address: data.address ?? undefined,
          city: data.city ?? undefined,
          state: data.state ?? undefined,
          zip_code: data.zip_code ?? undefined,
          country: data.country ?? undefined,
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  // Update the signed-in user's own profile row (name, contact and address
  // details collected at sign-up or edited later in Settings → Account).
  const updateProfile = async (updates: ProfileUpdates): Promise<void> => {
    if (!user) throw new Error('You must be signed in to update your profile.');
    // .select() makes the update return the row — if RLS silently filters
    // the target, zero rows come back and we report a failure instead of
    // pretending the save worked.
    const { data, error } = await supabase
      .from('user_profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', user.id)
      .select()
      .single();
    if (error) throw error;
    if (!data) {
      throw new Error('No changes were saved — you may not have permission to edit this profile.');
    }
    // Reload the profile so the app reflects the saved values
    const fresh = await fetchProfile(user);
    if (fresh) setProfile(fresh);
  };

  useEffect(() => {
    // Skip Supabase entirely if not configured — use demo mode directly
    if (!isSupabaseConfigured) {
      applyUser(null);
      setProfile({ id: 'demo', role: getDemoRole() });
      setProfileReady(true);
      setLoading(false);
      return;
    }

    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Session error:', error);
          applyUser(null);
        } else {
          applyUser(session?.user ?? null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        applyUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Only update the user here — profile fetching happens in a separate
    // effect so we never run awaited queries inside onAuthStateChange
    // (which can stall Supabase's auth lock).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        applyUser(null);
      } else if (session?.user) {
        applyUser(session.user);
      } else if (event !== 'INITIAL_SESSION' && event !== 'PASSWORD_RECOVERY') {
        applyUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the profile whenever the signed-in user ID changes (real mode
  // only). Keyed on the identity, not the user object, so token refreshes
  // don't clear the profile or unmount the current screen.
  useEffect(() => {
    if (!isSupabaseConfigured) {
      setProfileReady(true);
      return;
    }
    const userId = user?.id ?? null;
    if (!userId) {
      // applyUser already reset the profile for signed-out state
      return;
    }
    let cancelled = false;
    fetchProfile(user).then((p) => {
      if (!cancelled && userIdRef.current === userId) {
        setProfile(p);
        setProfileReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id, isSupabaseConfigured]); // eslint-disable-line react-hooks/exhaustive-deps

  const signIn = async (email: string, password: string, rememberMe?: boolean): Promise<string | null> => {
    // Skip Supabase entirely if not configured — use demo mode directly
    if (!isSupabaseConfigured) {
      console.log('Demo mode: Supabase not configured, simulating login');
      setUser({ id: 'demo-user', email } as User);
      setProfile({ id: 'demo-user', role: getDemoRole() });
      setProfileReady(true);
      return getDemoRole();
    }

    // Real mode: fail closed on invalid credentials
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      throw error;
    }

    const userProfile = await fetchProfile(data.user);
    if (!userProfile) {
      // Authenticated, but no profile row — the account is not fully set up
      await supabase.auth.signOut();
      throw new Error('This account is not fully set up yet. Please contact your administrator.');
    }

    applyUser(data.user);
    setProfile(userProfile);
    setProfileReady(true);
    return userProfile.role;
  };

  const signUp = async (email: string, password: string, metadata?: Record<string, any>): Promise<boolean> => {
    if (!isSupabaseConfigured) {
      console.log('Demo mode: Supabase not configured, simulating sign up');
      setUser({ id: 'demo-user', email } as User);
      setProfile({ id: 'demo-user', role: 'user' });
      setProfileReady(true);
      return true;
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: metadata },
    });

    if (error) throw error;

    // When email confirmation is required, there is no session yet — the user
    // must confirm their email before signing in. Do not treat them as signed in.
    if (!data.session) {
      return false;
    }

    applyUser(data.user);
    // Profile row is created by the database trigger; load it
    const userProfile = data.user ? await fetchProfile(data.user) : null;
    setProfile(userProfile);
    setProfileReady(true);
    return true;
  };

  const signOut = async () => {
    try {
      if (isSupabaseConfigured) {
        await supabase.auth.signOut();
      }
    } catch (error: any) {
      console.error('Sign out error:', error);
    } finally {
      applyUser(null);
      setProfile(isSupabaseConfigured ? null : { id: 'demo', role: getDemoRole() });
      setProfileReady(true);
      localStorage.removeItem('userRole');
      localStorage.removeItem('userName');
    }
  };

  const resetPassword = async (email: string) => {
    if (!isSupabaseConfigured) {
      console.log('Demo mode: password reset simulated');
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) throw error;
    } catch (error: any) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  const updatePassword = async (password: string) => {
    if (!isSupabaseConfigured) {
      console.log('Demo mode: password update simulated');
      return;
    }

    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  };

  const hasRole = (roles: string[]): boolean => {
    // The role comes exclusively from the user_profiles table (real mode)
    // or the demo selector (demo mode). No localStorage fallback.
    return !!profile && roles.includes(profile.role);
  };

  const logAudit = async (action: string, resource: string, resourceId?: string, details?: any) => {
    try {
      if (isSupabaseConfigured && user) {
        const { error } = await supabase.from('audit_logs').insert({
          user_id: user.id,
          user_email: user.email ?? '',
          user_role: profile?.role ?? 'client',
          action,
          resource_type: resource,
          resource_id: resourceId ?? null,
          details: details ?? null,
        });
        if (error) throw error;
      } else {
        console.log('Audit log:', { action, resource, resourceId, details, userId: user?.id || 'demo' });
      }
    } catch (error) {
      console.error('Audit log error:', error);
    }
  };

  const value = {
    user,
    profile,
    loading,
    profileReady,
    signIn,
    signUp,
    updateProfile,
    signOut,
    resetPassword,
    updatePassword,
    hasRole,
    logAudit,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
